import { Box, VStack, HStack, Text, Button, Avatar, Grid, GridItem, Badge, Spinner, Image, useColorModeValue, Tabs, TabList, Tab, TabPanels, TabPanel, useBreakpointValue } from '@chakra-ui/react';
import { FiEdit, FiArrowLeft } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, type UserProfile } from '../services/userService';
import { getSubscriptions } from '../services/subscriptionsService';
import { getUserProfile as getCreatorProfile } from '../services/userService';
import { useWatchlistStream } from '../hooks/useWatchlistStream';
import { getUserBookLibrary, type BookLibraryItem } from '../services/bookLibraryService';
import EditProfileDialog from '../components/EditProfileDialog';
import { getPublisherStats } from '../services/subscriptionService';

interface SubscribedCreator {
  id: string;
  name: string;
  avatar?: string;
  followers: number;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [subscribedCreators, setSubscribedCreators] = useState<SubscribedCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookLibrary, setBookLibrary] = useState<BookLibraryItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [userStats, setUserStats] = useState({
    totalViews: 0,
    totalLikes: 0,
    totalShares: 0,
    totalContent: 0
  });

  // Use StreamBuilder hook for real-time watchlist data
  const { items: watchlistItems } = useWatchlistStream(user?.uid || null);

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');
  const lightTextColor = useColorModeValue('gray.600', 'gray.400');
  const statsBgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Breakpoint values
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Load user profile (one-time)
    const loadUserProfile = async () => {
      try {
        const userProfile = await getUserProfile(user.uid);
        setProfile(userProfile || { uid: user.uid, name: user.displayName || '', email: user.email || '' });
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    // Load subscriptions (one-time)
    const loadSubscriptions = async () => {
      try {
        const subscriptions = await getSubscriptions(user.uid);
        const creatorProfiles = await Promise.all(
          subscriptions.map(sub => getCreatorProfile(sub.creatorId))
        );

        const creatorsWithProfiles = subscriptions.map((sub, index) => ({
          id: sub.creatorId,
          name: creatorProfiles[index]?.name || 'Unknown Creator',
          avatar: creatorProfiles[index]?.avatarUrl,
          followers: 0 // TODO: Implement follower count
        }));
        setSubscribedCreators(creatorsWithProfiles);
      } catch (error) {
        console.error('Error loading subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };

    // Load book library (real-time)
    const unsubscribeBookLibrary = getUserBookLibrary(user.uid, (books) => {
      setBookLibrary(books);
    });

    // Load user stats
    const loadUserStats = async () => {
      try {
        const stats = await getPublisherStats(user.uid);
        setUserStats({
          totalViews: stats.views,
          totalLikes: stats.likes,
          totalShares: stats.shares,
          totalContent: stats.totalContent
        });
      } catch (error) {
        console.error('Error loading user stats:', error);
      }
    };

    // Load initial data
    loadUserProfile();
    loadSubscriptions();
    loadUserStats();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeBookLibrary) {
        unsubscribeBookLibrary();
      }
    };
  }, [user]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" className="page-content">
      {/* Header */}
      <HStack justify="space-between" align="center" p={4} borderBottom="1px" borderColor={borderColor}>
        {!isMobile ? (
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              leftIcon={<IconWrapper icon={FiArrowLeft} size={16} />}
            >
              Back
            </Button>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Profile
            </Text>
          </HStack>
        ) : (
          <Box /> // Empty box for mobile to maintain spacing
        )}
        <HStack gap={2}>
          {isMobile ? (
            <Button 
              variant="outline" 
              colorScheme="purple" 
              size="sm" 
              onClick={() => setIsEditOpen(true)}
              p={2}
            >
              <IconWrapper icon={FiEdit} size={16} />
            </Button>
          ) : (
            <Button variant="outline" colorScheme="purple" size="sm" onClick={() => setIsEditOpen(true)}>
              <IconWrapper icon={FiEdit} size={16} style={{ marginRight: '8px' }} />
              Quick Edit
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Profile Content */}
      <Box maxW="1200px" mx="auto" px={4} py={8}>
        <VStack gap={8} align="center" mb={8}>
          {/* Profile Picture */}
          <Avatar
            size="2xl"
            src={profile?.avatarUrl || ''}
            name={profile?.name || 'User'}
            bg="purple.500"
            color="white"
            fontSize="4xl"
            fontWeight="bold"
          />
          
          {/* Name and Email */}
          <VStack gap={2} align="center">
            <Text fontSize="3xl" fontWeight="bold" color={textColor}>
              {profile?.name || 'Unnamed User'}
            </Text>
            <Text color={lightTextColor} fontSize="lg">
              {profile?.email || 'No email provided'}
            </Text>
          </VStack>

          {/* Bio */}
          {profile?.bio && (
          <Text 
            color={lightTextColor} 
            fontSize="md" 
            textAlign="center" 
            maxW="600px" 
            lineHeight="1.6"
          >
              {profile.bio}
          </Text>
          )}

          {/* Statistics */}
          <Box bg={statsBgColor} p={{ base: 4, md: 8 }} borderRadius="xl" w="100%" maxW="600px" border="1px" borderColor={borderColor}>
            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={{ base: 4, md: 6 }}>
              <GridItem>
                <VStack gap={1}>
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {userStats.totalViews.toLocaleString()}
                  </Text>
                  <Text fontSize="sm" color={lightTextColor} textTransform="uppercase" letterSpacing="wide">
                    Views
                  </Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack gap={1}>
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {userStats.totalLikes.toLocaleString()}
                  </Text>
                  <Text fontSize="sm" color={lightTextColor} textTransform="uppercase" letterSpacing="wide">
                    Likes
                  </Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack gap={1}>
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {userStats.totalShares.toLocaleString()}
                  </Text>
                  <Text fontSize="sm" color={lightTextColor} textTransform="uppercase" letterSpacing="wide">
                    Shares
                  </Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack gap={1}>
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {userStats.totalContent}
                  </Text>
                  <Text fontSize="sm" color={lightTextColor} textTransform="uppercase" letterSpacing="wide">
                    Content
                  </Text>
                </VStack>
              </GridItem>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <VStack gap={3} w="100%" maxW="400px">
            <Button
              colorScheme="purple"
              size="lg"
              borderRadius="full"
              w="100%"
              onClick={() => navigate('/creator-dashboard')}
            >
              Creator Dashboard
            </Button>
            <Button
              variant="outline"
              colorScheme="purple"
              size="lg"
              borderRadius="full"
              w="100%"
              onClick={() => navigate('/subscription-management')}
            >
              Manage Subscription
            </Button>
            <Button
              variant="outline"
              colorScheme="gray"
              size="lg"
              borderRadius="full"
              w="100%"
              onClick={() => navigate('/analytics')}
            >
              View Analytics
            </Button>
          </VStack>
        </VStack>

        {/* Content Tabs */}
        <Tabs variant="enclosed" w="100%" index={activeTab} onChange={setActiveTab}>
          <TabList 
            mb={6} 
            bg={statsBgColor} 
            borderRadius="lg" 
            p={1} 
            overflowX="auto" 
            scrollBehavior="smooth"
            css={{
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#A0AEC0',
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#718096',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#A0AEC0 transparent',
            }}
          >
            <Tab fontSize={{ base: "sm", md: "md" }} px={{ base: 3, md: 6 }} py={3} minW="fit-content">
              My Watchlist
            </Tab>
            <Tab fontSize={{ base: "sm", md: "md" }} px={{ base: 3, md: 6 }} py={3} minW="fit-content">
              My Book Library
            </Tab>
            <Tab fontSize={{ base: "sm", md: "md" }} px={{ base: 3, md: 6 }} py={3} minW="fit-content">
              Followed Creators
            </Tab>
          </TabList>

          <TabPanels>
            {/* Watchlist Tab */}
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              {watchlistItems.length === 0 ? (
                <Text color={lightTextColor} textAlign="center" py={8}>
                  Your watchlist is empty. Add some content to get started!
                </Text>
              ) : (
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }} gap={{ base: 3, md: 4 }}>
                  {watchlistItems.map((item) => (
                    <GridItem key={item.contentId}>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        overflow="hidden"
                        cursor="pointer"
                        border="1px"
                        borderColor={borderColor}
                        _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                        transition="all 0.2s"
                      >
                        <Box
                          h="120px"
                          bgImage={item.content?.thumbnail || item.content?.poster ? `url(${item.content.thumbnail || item.content.poster})` : undefined}
                          bgSize="cover"
                          backgroundPosition="center"
                          bg="gray.100"
                        />
                        <Box p={3}>
                          <Text fontSize="sm" fontWeight="medium" color={textColor} isTruncated>
                            {item.content?.title || item.title || 'Untitled'}
                          </Text>
                          <Badge colorScheme="purple" size="sm" mt={1}>
                            {item.content?.category || 'unknown'}
                          </Badge>
                        </Box>
                      </Box>
                    </GridItem>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Book Library Tab */}
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              {bookLibrary.length === 0 ? (
                <Text color={lightTextColor} textAlign="center" py={8}>
                  No books in your library yet. Start reading by adding books to your library!
                </Text>
              ) : (
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }} gap={{ base: 3, md: 4 }}>
                  {bookLibrary.map((book) => (
                    <GridItem key={book.id}>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        overflow="hidden"
                        cursor="pointer"
                        border="1px"
                        borderColor={borderColor}
                        _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                        transition="all 0.2s"
                        onClick={() => window.location.href = `/book/${book.bookId}`}
                      >
                        <Image
                          src={book.bookCover}
                          alt={book.bookTitle}
                          w="100%"
                          h="200px"
                          objectFit="cover"
                          backgroundPosition="center"
                          bg="gray.100"
                        />
                        <Box p={3}>
                          <Text fontSize="sm" fontWeight="medium" color={textColor} isTruncated>
                            {book.bookTitle}
                          </Text>
                          <Text fontSize="xs" color={lightTextColor} isTruncated>
                            by {book.bookAuthor}
                          </Text>
                          <Badge colorScheme="purple" size="sm" mt={1}>
                            Book
                          </Badge>
                        </Box>
                      </Box>
                    </GridItem>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Followed Creators Tab */}
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              {subscribedCreators.length === 0 ? (
                <Text color={lightTextColor} textAlign="center" py={8}>
                  You haven't followed any creators yet. Discover and follow your favorite creators!
                </Text>
              ) : (
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={{ base: 3, md: 4 }}>
                  {subscribedCreators.map((creator) => (
                    <GridItem key={creator.id}>
                      <Box
                        bg={cardBg}
                        p={6}
                        borderRadius="lg"
                        textAlign="center"
                        cursor="pointer"
                        border="1px"
                        borderColor={borderColor}
                        _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                        transition="all 0.2s"
                      >
                        <Avatar
                          size="lg"
                          src={creator.avatar}
                          name={creator.name}
                          mx="auto"
                          mb={3}
                        />
                        <Text fontSize="lg" fontWeight="medium" color={textColor}>
                          {creator.name}
                        </Text>
                        <Text fontSize="sm" color={lightTextColor}>
                          {creator.followers} followers
                        </Text>
                      </Box>
                    </GridItem>
                  ))}
                </Grid>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <EditProfileDialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        initialProfile={profile}
        onSaved={(updates) => setProfile((p) => ({ ...(p || { uid: user?.uid || '' }), ...updates }))}
      />
    </Box>
  );
};

export default Profile;
